/**
 * Shared Zod schemas for dataset references in LLM-facing tool input schemas.
 *
 * Every dataset-producing tool stages its result under an `outputDataset`;
 * dataset references inside an s-expression resolve by binding name against
 * the active branch's recipe projection — no top-level dataset-input slot is
 * required. Call sites override via Zod's native `.describe(...)` /
 * `.optional()` / `.min(...)` when they need tool-specific framing.
 *
 * Descriptions are intentionally tool-agnostic. Cross-tool references
 * ("use `transformDataset` to filter first", "paired with `getDatapoints`")
 * belong in each tool's `ToolGuidance` / `instructions` block — that's where
 * `.mention` resolves without the tool-registration import cycle that
 * plagues schema modules.
 */
import { z } from 'zod';

/** LLM-facing input: stage the produced dataset under this key. */
export const outputDatasetSchema = z
  .string()
  .describe('Unique key under which the produced dataset is staged in chat history for downstream tools to reference.');
/**
 * LLM-facing input: a single s-expression string.
 *
 * SSOT for the `expression` tool-input field. The base type/constraint
 * (`z.string().min(1)`) lives here once; each tool appends its own
 * `.describe(...)` for tool-specific grammar guidance.
 */
export const expressionSchema = z.string().min(1);
